// Ad-level settings (rotation, flight dates, hard cutoff, dynamic targeting
// keys, and the even/custom weighting choice) for the Smart Traffic flow.
//
// In practice these are the same across every ad in a campaign, so one shared
// set is the default and each ad can opt out. The alternative — every ad
// independent — meant retyping the same dates once per spot length.
//
// Pure on purpose: the stepper holds this in a ref and the per-ad options
// steps read and write the single global creatives slice, which makes the
// inheritance rules the easy thing to get wrong and the hard thing to see.
//
// Note what is NOT shared: per-creative weight values. Ads hold different
// creatives, so a number keyed to one ad's creative means nothing in another.
// Only the EVEN/CUSTOM choice follows the baseline; the values stay per ad,
// living in the creatives slice where AssetWeight already writes them.

/**
 * Snapshot the ad-level fields out of the creatives slice.
 * @param {object} creativesState - state.creatives
 */
export function captureAdSettings(creativesState) {
  const cs = creativesState || {};
  return {
    rotationType: cs.rotationType,
    weightDistribution: cs.weightDistribution,
    startDate: cs.startDate,
    endDate: cs.endDate,
    hardCutoff: cs.hardCutoff,
    dynamicTargetingKeys: cs.dynamicTargetingKeys
  };
}

/**
 * The starting baseline: app defaults, but campaign dates from the slice.
 * @param {object} creativesState - state.creatives
 * @param {object} defaults - { rotationType, weightDistribution }
 */
export function defaultAdSettings(creativesState, defaults) {
  const cs = creativesState || {};
  const d = defaults || {};
  return {
    rotationType: d.rotationType,
    weightDistribution: d.weightDistribution,
    startDate: cs.startDate,
    endDate: cs.endDate,
    hardCutoff: false,
    dynamicTargetingKeys: []
  };
}

export function createAdSettingsState(shared) {
  return { shared: shared || null, perAd: {}, customized: {} };
}

export function isAdCustomized(state, key) {
  return !!(state && key && state.customized[key]);
}

/**
 * The settings an ad should show and submit with.
 *
 * A customized ad with nothing stored still falls back to shared rather than
 * to null — the only way to reach that is a key changing under the user, and
 * showing the baseline beats showing blank fields.
 */
export function resolveAdSettings(state, key) {
  if (!state) return null;
  if (isAdCustomized(state, key) && state.perAd[key]) return state.perAd[key];
  return state.shared;
}

/**
 * Store what the user just edited, against whichever scope the ad follows.
 *
 * This is the whole point of the feature: editing an ad that follows the
 * baseline moves the baseline, so every other following ad moves with it.
 */
export function recordAdSettings(state, key, settings) {
  if (!state || !key) return state;

  if (isAdCustomized(state, key)) {
    return {
      ...state,
      perAd: { ...state.perAd, [key]: settings }
    };
  }

  return { ...state, shared: settings };
}

/**
 * Opt an ad in or out of the shared baseline.
 *
 * Opting out seeds the ad from what is on screen, so unchecking the box never
 * changes any value — it only stops later baseline edits from reaching this
 * ad. Opting back in drops the ad's own copy, which is what "use shared
 * settings" has to mean for the checkbox to be reversible.
 */
export function setAdCustomized(state, key, customized, currentSettings) {
  if (!state || !key) return state;

  if (customized) {
    return {
      ...state,
      customized: { ...state.customized, [key]: true },
      perAd: {
        ...state.perAd,
        [key]: currentSettings || state.perAd[key] || state.shared
      }
    };
  }

  const nextCustomized = { ...state.customized };
  const nextPerAd = { ...state.perAd };
  delete nextCustomized[key];
  delete nextPerAd[key];

  return { ...state, customized: nextCustomized, perAd: nextPerAd };
}

/**
 * How many ads follow the baseline, for the "applies to N ads" hint.
 */
export function countSharedAds(state, keys) {
  return (keys || []).filter((key) => !isAdCustomized(state, key)).length;
}
